import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, BadgeCheck, Users } from "lucide-react";
import { EASE } from "@/lib/site";
import QuoteButton from "@/components/ui-kit/QuoteButton";
import { Eyebrow } from "@/components/ui-kit/Reveal";
import HeroFrame from "./HeroFrame";

const LINES = [
  { text: "Protecting", cls: "text-[var(--ink)]" },
  { text: "Every Sector,", cls: "text-[var(--navy)]" },
  { text: "Securing Every", cls: "text-[var(--ink)]" },
  { text: "Moment.", cls: "text-[var(--red)]" },
];

const PROOF = [
  { icon: ShieldCheck, label: "PSARA Licensed" },
  { icon: BadgeCheck, label: "CAPSI Registered" },
  { icon: Users, label: "SAT Registered" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-[var(--paper)]">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img src="/images/hero-building.jpg" alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-white/85 lg:bg-transparent lg:bg-[linear-gradient(90deg,#F7F8FB_0%,#F7F8FB_45%,rgba(247,248,251,0.75)_65%,rgba(247,248,251,0.15)_100%)]" />
      <div aria-hidden className="absolute left-[-10%] bottom-24 w-[70%] h-[3px] bg-[var(--red)] -rotate-[12deg] origin-left" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-16 pb-24 sm:pt-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
            <Eyebrow>Security &amp; Facility Management</Eyebrow>
          </motion.div>
          <h1 data-testid="hero-headline" className="mt-6 font-display italic font-black uppercase leading-[0.88] tracking-tight text-[2.9rem] sm:text-[5rem] lg:text-[5.6rem] xl:text-[6.2rem]">
            {LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1 pr-4">
                <motion.span className={`block ${l.cls}`} initial={{ y: "110%", skewY: 6 }} animate={{ y: 0, skewY: 0 }}
                  transition={{ duration: 1.1, delay: 0.2 + i * 0.13, ease: EASE }}>{l.text}</motion.span>
              </span>
            ))}
          </h1>
          <motion.p data-testid="hero-subheadline" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-slate-700 leading-relaxed">
            S4s Enterprises Pvt. Ltd. provides Security, Facility Management which are essential to the Functioning of a vibrant and healthy economy. We have Emerged as a trusted leader across these business segments in Telangana, India
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4">
            <QuoteButton testId="hero-request-quote-button" />
            <QuoteButton testId="hero-explore-services-button" to="/services" label="Our Services" variant="navy" />
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
            {PROOF.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Icon className="w-5 h-5 text-[var(--navy)]" /> {label}
              </li>
            ))}
          </motion.ul>
        </div>
        <div className="lg:col-span-5">
          <HeroFrame />
        </div>
      </div>
    </section>
  );
}
